import React from 'react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';

interface ContactInfoData {
  title?: string;
  subtitle?: string;
  description?: string;
  businessName?: string;
  address?: string;
  phone?: string;
  email?: string;
  hours?: string;
  showMap?: boolean;
  mapEmbedUrl?: string;
  layout?: string;
  horizontalPadding?: string;
  backgroundColor?: string;
  textColor?: string;
  accentColor?: string;
  customColors?: {
    background?: string;
    title?: string;
    text?: string;
    accent?: string;
  };
  buttons?: ButtonData[];
}

interface ContactInfoEditorProps {
  formData: ContactInfoData;
  setFormData: (data: ContactInfoData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

export const ContactInfoEditor: React.FC<ContactInfoEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => {
  const data = formData;
  const buttons = data.buttons || [];
  const themeColors = useCustomColors();

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  const handleMapEmbedChange = (value: string) => {
    // Check if the pasted content is an iframe HTML
    if (value.includes('<iframe') && value.includes('src=')) {
      // Extract the src URL from the iframe
      const srcMatch = value.match(/src="([^"]+)"/);
      if (srcMatch && srcMatch[1]) {
        setFormData({ ...data, mapEmbedUrl: srcMatch[1] });
      } else {
        setFormData({ ...data, mapEmbedUrl: value });
      }
    } else {
      setFormData({ ...data, mapEmbedUrl: value });
    }
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('contact-info', themeColors);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <HeaderSection data={data} setFormData={setFormData} />

      {/* Contact Information */}
      <ContactDetailsSection data={data} setFormData={setFormData} />

      {/* Map Settings */}
      <MapSettingsSection
        data={data}
        setFormData={setFormData}
        onMapEmbedChange={handleMapEmbedChange}
      />

      {/* Call-to-Action Buttons */}
      <ButtonsEditor
        buttons={buttons}
        onChange={newButtons => setFormData({ ...data, buttons: newButtons })}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        label="Call-to-Action Buttons (Optional)"
      />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize contact info colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface HeaderSectionProps {
  data: ContactInfoData;
  setFormData: (data: ContactInfoData) => void;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Header</h3>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
        <input
          type="text"
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Contact Us"
          maxLength={200}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
        <input
          type="text"
          value={data.subtitle || ''}
          onChange={e => setFormData({ ...data, subtitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Get in Touch"
          maxLength={500}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={data.description || ''}
          onChange={e => setFormData({ ...data, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="We love our customers, so feel free to visit during normal business hours."
          rows={3}
          maxLength={1000}
        />
      </div>
    </div>
  </div>
);

interface ContactDetailsSectionProps {
  data: ContactInfoData;
  setFormData: (data: ContactInfoData) => void;
}

const ContactDetailsSection: React.FC<ContactDetailsSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
        <input
          type="text"
          value={data.businessName || ''}
          onChange={e => setFormData({ ...data, businessName: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Your Business Name"
          maxLength={200}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
        <textarea
          value={data.address || ''}
          onChange={e => setFormData({ ...data, address: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="123 Main Street, City, State 12345, USA"
          rows={2}
          maxLength={500}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
        <input
          type="tel"
          value={data.phone || ''}
          onChange={e => setFormData({ ...data, phone: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="(555) 123-4567"
          maxLength={50}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
        <input
          type="email"
          value={data.email || ''}
          onChange={e => setFormData({ ...data, email: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="contact@yourbusiness.com"
          maxLength={100}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Hours</label>
        <textarea
          value={data.hours || ''}
          onChange={e => setFormData({ ...data, hours: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Open today: 09:00 am – 05:00 pm"
          rows={3}
          maxLength={500}
        />
        <p className="text-xs text-gray-500 mt-1">You can use multiple lines for different days</p>
      </div>
    </div>
  </div>
);

interface MapSettingsSectionProps {
  data: ContactInfoData;
  setFormData: (data: ContactInfoData) => void;
  onMapEmbedChange: (value: string) => void;
}

const MapSettingsSection: React.FC<MapSettingsSectionProps> = ({
  data,
  setFormData,
  onMapEmbedChange,
}) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Map Settings</h3>
    <div className="space-y-4">
      <div className="flex items-center">
        <input
          type="checkbox"
          id="showMap"
          checked={data.showMap !== false}
          onChange={e => setFormData({ ...data, showMap: e.target.checked })}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="showMap" className="ml-2 block text-sm text-gray-700">
          Show Map
        </label>
      </div>

      {data.showMap !== false && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Google Maps Embed URL
          </label>
          <input
            type="url"
            value={data.mapEmbedUrl || ''}
            onChange={e => onMapEmbedChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="https://www.google.com/maps/embed?pb=..."
            maxLength={2000}
          />
          <p className="text-xs text-gray-500 mt-1">
            Paste either the embed URL or the entire iframe code from Google Maps → Share → Embed a
            map
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Layout</label>
        <select
          value={data.layout || 'map-right'}
          onChange={e => setFormData({ ...data, layout: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="map-right">Contact Info Left, Map Right</option>
          <option value="map-left">Map Left, Contact Info Right</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Side Padding</label>
        <select
          value={data.horizontalPadding || 'md'}
          onChange={e => setFormData({ ...data, horizontalPadding: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="none">None</option>
          <option value="xs">Extra Small</option>
          <option value="sm">Small</option>
          <option value="md">Normal</option>
          <option value="lg">Large</option>
          <option value="xl">Extra Large</option>
          <option value="2xl">2X Large</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">Left and right padding</p>
      </div>
    </div>
  </div>
);
